import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useCatalogList } from '../../../smartLinks/catalog/useCatalogList';
import { PromoteContainer } from '../PromoteContainer';
import { SMLBackLink } from '../../components/SMLBackLink/SMLBackLink';
import { postsMessages } from '../posts/postsMessages';
import { usePromoteChannels } from '../hooks/usePromoteChannels';
import { getPromoteMediaDraft, mediaKindOfDraft, PromoteMediaDraft } from '../draftStore';
import { isCaptionOverLimit } from '../platformMeta';
import { useComposerDraft } from './useComposerDraft';
import { useComposerEvents } from './useComposerEvents';
import { useComposerActions } from './useComposerActions';
import { useComposerConnect } from './useComposerConnect';
import { useComposerPostMedia } from './useComposerPostMedia';
import { useComposerEditPost } from './useComposerEditPost';
import { ComposerChannelsPanel } from './ComposerChannelsPanel';
import { ComposerCopyPanel } from './ComposerCopyPanel';
import { ComposerMediaPanel } from './ComposerMediaPanel';
import { ComposerReleasePanel } from './ComposerReleasePanel';
import { isDemoAccountId } from '../../../accountSelector/demoAccounts';
import { useIsReadOnlyAccount } from '../../../accountSelector/useIsReadOnlyAccount';
import { ComposerPreviews } from './ComposerPreviews';
import { ComposerAttachMediaModal, pickerReleaseOf } from './ComposerAttachMediaModal';
import { ComposerMediaWarning } from './ComposerMediaWarning';
import { promoPostMediaGoneMessage, promoPostNeedsMediaPlatforms } from '../promoPostErrors';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { ComposerTikTokSection } from './tiktok/ComposerTikTokSection';
import { tiktokMessages } from './tiktok/tiktokMessages';
import { ComposerAccountSection, AccountSectionPlatform } from './accounts/ComposerAccountSection';
import { channelPostsThroughApi, useComposerPlatformSettings } from './settings/useComposerPlatformSettings';
import { ComposerYouTubeSection } from './youtube/ComposerYouTubeSection';
import { postSettingsErrorMessage } from '../postSettingsErrors';
import { xMonthLimitMessage } from '../xPostsLimit';
import { WatermarkPostWarning } from '../../watermarkUpsell/files/WatermarkPostWarning';
import { watermarkFileMessages } from '../../watermarkUpsell/files/watermarkFileMessages';
import { useWatermarkChoice } from '../../watermarkUpsell/choice/useWatermarkChoice';
import { LimitUpsell } from '../../limitUpsell/LimitUpsell';
import { useComposerPostQuota } from './useComposerPostQuota';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PromoteComposerScreenProps {
  accountId: number;
}

const PromoteComposerScreenComponent: React.FC<PromoteComposerScreenProps> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();

  const { markResult } = useComposerEvents();
  const { channels, saveHandle, isWriting: isSavingHandle } = usePromoteChannels({ accountId });
  const catalog = useCatalogList({ accountId });
  const draft = useComposerDraft({ channels, items: catalog.items });
  const actions = useComposerActions({ accountId, draft });
  // "Edit in Composer" of the Calendar opens one existing post (`?postUuid=`).
  const edit = useComposerEditPost({ accountId, draft, items: catalog.items });
  const connect = useComposerConnect({ saveHandle, onConnected: draft.togglePlatform });
  // The measures of the attached video, for the format line of each channel. They never block
  // scheduling: a file with no measures goes out as a normal video.
  const postMedia = useComposerPostMedia(draft.media);
  // The settings of the platforms that want the user's own choice (TikTok Direct Post, design
  // music24-composer-tiktok). A platform whose choice is not complete stops "Schedule".
  const platformSettings = useComposerPlatformSettings({ accountId, draft, editPost: edit.post });
  const { showSuccess } = useShowToast();
  const isReadOnlyArtist = useIsReadOnlyAccount();
  const { canUseX } = useMusic24Capabilities();
  // Instagram, Facebook Pages, Threads and X show the account that gets the post (Meta and Threads
  // app review; design x-connector c3) when the channel posts through the API. X only on an X plan.
  const accountSections = useMemo(
    () =>
      draft.platforms.flatMap((platform) => {
        const channel = draft.usable.find((one) => one.platform === platform);
        const hasSection =
          platform === 'instagram' ||
          platform === 'facebook' ||
          platform === 'threads' ||
          (platform === 'x' && canUseX);
        if (hasSection && channel !== undefined && channelPostsThroughApi(channel)) {
          return [{ platform: platform as AccountSectionPlatform, channel }];
        }
        return [];
      }),
    [canUseX, draft.platforms, draft.usable]
  );
  const youtubeChannel = draft.usable.find((channel) => channel.platform === 'youtube');

  const [isPickerOpen, setPickerOpen] = useState(false);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  // The empty state of the picker: the Studio, on the song of the picker when one is picked.
  const openStudio = useCallback(
    (catalogItemUuid: string | undefined) =>
      router.push(SMLRoutes.promoteStudio, catalogItemUuid === undefined ? undefined : { catalogItemUuid }),
    [router]
  );

  const { attachMedia } = draft;
  const { clearAiError } = actions;
  const attach = useCallback(
    (media: PromoteMediaDraft) => {
      attachMedia(media);
      clearAiError();
      setPickerOpen(false);
    },
    [attachMedia, clearAiError]
  );

  const writeMaster = useCallback(() => {
    actions.writeMaster().catch(console.error);
  }, [actions]);

  const tailor = useCallback(() => {
    actions.tailorPerPlatform().catch(console.error);
  }, [actions]);

  const send = useCallback(
    (asDraft: boolean) => {
      const sentToTikTok = !asDraft && platformSettings.sections.includes('tiktok');
      const afterSave = (saved: boolean) => {
        if (saved && sentToTikTok) {
          // TikTok G8: the post is not on the profile at once.
          showSuccess(intl.formatMessage(tiktokMessages.processing));
        }
        return saved;
      };
      if (edit.isEditing) {
        const postUuid = edit.post?.uuid;
        edit
          .save(asDraft, platformSettings.settings)
          .then((saved) => (afterSave(saved) ? router.push(SMLRoutes.promoteCalendar, { post: postUuid }) : undefined))
          .catch(console.error);
        return;
      }
      actions
        .publish(asDraft, platformSettings.settings)
        .then((created) => {
          if (created) {
            markResult(asDraft ? 'draft' : 'posted');
          }
          return afterSave(created) ? router.push(SMLRoutes.promoteCalendar) : undefined;
        })
        .catch(console.error);
    },
    [actions, edit, intl, markResult, platformSettings.sections, platformSettings.settings, router, showSuccess]
  );

  const publish = useCallback(() => send(false), [send]);
  const saveDraft = useCallback(() => send(true), [send]);

  // A caption over a platform's ceiling is rejected by that platform, not by
  // us, so it is stopped here rather than after the post is created. X counts a link as 23.
  const isOverAnyLimit = useMemo(
    () => draft.platforms.some((platform) => isCaptionOverLimit(platform, draft.captionFor(platform))),
    [draft]
  );

  // A story is the picture or the clip, so it cannot go out without one.
  // An edited post keeps its own media, so the media rules of the server judge it.
  const isStoryWithoutMedia = draft.format === 'story' && draft.media === undefined && !edit.isEditing;

  // Instagram, TikTok and YouTube cannot post text only. A draft may wait for its media; a
  // scheduled post may not, because it would fail at its hour (design product-gaps b1).
  const isMissingMedia = draft.missingMedia.length > 0 && !edit.isEditing;
  // An image that a platform refuses (an Instagram feed image wider than 1.91:1) would fail at its
  // hour too. The format line of the channel says why.
  const isImageRefused = draft.imageRefused.length > 0 && !edit.isEditing;
  // A video that a platform refuses (over 64 MB for TikTok, a story over 60 s) would fail too.
  const isVideoRefused = draft.videoRefused.length > 0 && !edit.isEditing;

  // design: limit-upsell a10. A new post takes one post of this month for each channel.
  const postQuota = useComposerPostQuota({
    accountId,
    channelCount: draft.platforms.length,
    isEditing: edit.isEditing,
    writeError: edit.error ?? actions.error,
  });

  const canSave = draft.platforms.length > 0 && draft.master.trim() !== '' && !isOverAnyLimit && !isStoryWithoutMedia;
  const needsSettings = platformSettings.blocking.length > 0;
  const canPublish =
    canSave &&
    !isMissingMedia &&
    !isImageRefused &&
    !isVideoRefused &&
    !needsSettings &&
    postQuota.shortfall === null &&
    (draft.timing !== 'pick' || draft.pickedAt !== '');

  // The server refuses the same post with PromoPostNeedsMedia (design product-gaps b2).
  const writeError = edit.error ?? actions.error;
  const refusedForMedia = promoPostNeedsMediaPlatforms(writeError);
  const isSending = actions.isPublishing || edit.isSaving;
  // An edit of a post that is not a draft saves the changes; a draft can stay a draft or be scheduled.
  const isEditingArmedPost = edit.isEditing && edit.post !== undefined && edit.post.status !== 'draft';

  // design: trial-upsell a5. A watermarked file on Free and the trial: the button says so and the
  // warning line shows. The post still schedules with one click.
  const { applies: watermarkApplies } = useWatermarkChoice();
  const hasWatermarkedMedia = watermarkApplies && draft.media?.watermark === true;

  // An edit saves the picked time only, so its button never says "Publish now".
  const publishLabel = isEditingArmedPost
    ? intl.formatMessage({ id: 'app.sml.promote.composer.edit.save', defaultMessage: 'Save the changes' })
    : draft.timing === 'now' && !edit.isEditing
      ? intl.formatMessage({ id: 'app.sml.promote.composer.timing.now', defaultMessage: 'Publish now' })
      : hasWatermarkedMedia
        ? intl.formatMessage(watermarkFileMessages.scheduleWithWatermark)
        : intl.formatMessage({ id: 'app.sml.promote.composer.schedule', defaultMessage: 'Schedule' });

  const saveDraftLabel = intl.formatMessage({ id: 'app.sml.promote.composer.saveDraft', defaultMessage: 'Save draft' });
  const postButtons = (
    <>
      {isEditingArmedPost ? null : (
        <SMLButton outline omitCap disabled={!canSave || isSending} onClick={saveDraft}>
          {saveDraftLabel}
        </SMLButton>
      )}
      <SMLButton
        color="primary"
        omitCap
        leftIcon={faPaperPlane}
        disabled={!canPublish || isSending}
        isLoading={isSending}
        onClick={publish}
      >
        {publishLabel}
      </SMLButton>
    </>
  );

  // design navigation-v2 c 3d: the Composer is a page under Posts, with a back link and no tabs.
  const postsTitle = intl.formatMessage(postsMessages.title);
  const pageTitle = edit.isEditing
    ? intl.formatMessage({ id: 'app.sml.promote.composer.edit.title', defaultMessage: 'Edit the post' })
    : intl.formatMessage(postsMessages.newPost);

  return (
    <PromoteContainer
      title={pageTitle}
      crumbs={[postsTitle, pageTitle]}
      back={<SMLBackLink to={SMLRoutes.promotePosts} destination={postsTitle} />}
      subtitle={intl.formatMessage({
        id: 'app.sml.promote.composer.intro',
        defaultMessage:
          'Write it once. Let it land differently on every platform, then send the whole set at the right hour.',
      })}
      actions={<div className="d-flex gap-2">{postButtons}</div>}
    >
      {isMissingMedia || refusedForMedia !== null ? (
        <ComposerMediaWarning
          platforms={isMissingMedia ? draft.missingMedia : (refusedForMedia ?? [])}
          onAttach={openPicker}
        />
      ) : null}
      {hasWatermarkedMedia && <WatermarkPostWarning variant="post" count={draft.platforms.length} className="mb-3" />}
      <LimitUpsell
        shortfall={postQuota.shortfall}
        context="post"
        fit={canSave && !isSending ? { label: saveDraftLabel, onClick: saveDraft } : null}
        className="mb-3"
      />
      {writeError !== undefined &&
        refusedForMedia === null &&
        !(postQuota.isQuotaError && postQuota.shortfall !== null) && (
          <ErrorAlert
            error={writeError}
            customMessage={
              postSettingsErrorMessage(intl, writeError) ??
              xMonthLimitMessage(intl, writeError) ??
              promoPostMediaGoneMessage(intl, writeError)
            }
          />
        )}

      <div className={styles.composerGrid}>
        <div className={styles.panelStack}>
          {/* The order of the steps (owner request 2026-10-09): the media decides the channels,
              the release lets the AI write, then the channels, then the copy. */}
          <ComposerMediaPanel accountId={accountId} channels={channels} draft={draft} onOpenMediaPicker={openPicker} />
          <ComposerReleasePanel items={catalog.items} value={draft.itemUuid} onChange={draft.setItemUuid} />
          <ComposerChannelsPanel
            channels={channels}
            selected={draft.platforms}
            connecting={connect.connecting}
            disabledPlatforms={draft.blockedByFormat}
            blockedSelectedPlatforms={draft.chosenBlockedByFormat}
            needsMediaPlatforms={draft.missingMedia}
            unavailablePlatforms={draft.mediaBlocked}
            oneOffPlatforms={draft.oneOffBlocked}
            isXMonthBlocked={draft.isXMonthBlocked}
            droppedPlatforms={draft.droppedForMedia}
            media={postMedia.media}
            isMeasuring={postMedia.isMeasuring}
            format={draft.format}
            isSaving={isSavingHandle}
            onToggle={draft.togglePlatform}
            onConnect={connect.open}
            onCancelConnect={connect.cancel}
            onSaveHandle={connect.save}
          />
          <ComposerCopyPanel
            draft={draft}
            isWriting={actions.isWriting}
            aiError={actions.aiError}
            isDemo={isDemoAccountId(accountId)}
            isReadOnly={isReadOnlyArtist}
            onWriteMaster={writeMaster}
            onTailor={tailor}
          />
          {platformSettings.sections.includes('tiktok') ? (
            <ComposerTikTokSection
              state={platformSettings.tiktok}
              mediaDurationSec={draft.media?.durationSec ?? null}
              hasMedia={draft.media !== undefined}
              footer={postButtons}
            />
          ) : null}
          {accountSections.map(({ platform, channel }) => (
            <ComposerAccountSection
              key={platform}
              platform={platform}
              channel={channel}
              caption={draft.captionFor(platform)}
              media={postMedia.media}
              format={draft.format}
            />
          ))}
          {youtubeChannel !== undefined && platformSettings.sections.includes('youtube') ? (
            <ComposerYouTubeSection state={platformSettings.youtube} channel={youtubeChannel} footer={postButtons} />
          ) : null}
        </div>
        <ComposerPreviews
          platforms={draft.platforms}
          channels={draft.usable}
          captionFor={draft.captionFor}
          bestSlots={draft.bestSlots}
          timing={draft.timing}
          pickedAt={draft.pickedAt}
          mediaUrl={draft.media?.fileUrl}
          mediaKind={draft.media === undefined ? undefined : mediaKindOfDraft(draft.media)}
        />
      </div>

      <ComposerAttachMediaModal
        isOpen={isPickerOpen}
        accountId={accountId}
        items={catalog.items ?? []}
        releaseUuid={pickerReleaseOf(draft.itemUuid, draft.media)}
        available={getPromoteMediaDraft()}
        closeModal={closePicker}
        onAttach={attach}
        onOpenStudio={openStudio}
      />
    </PromoteContainer>
  );
};

export const PromoteComposerScreen = TypedMemo(PromoteComposerScreenComponent);
